import React, { useMemo, useState } from 'react';
import IconButton from 'view/components/buttons/IconButton';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import AssignmentFeedback from 'models/assignments/AssignmentFeedback';
import useApiCall from 'contexts/ApiCall';
import teacherService from 'services/teacherService';
import AnnotationModal from 'view/components/teacher/assignment/grading/AnnotationModal';
import { useLanguage } from 'contexts/LanguageContext';

export interface FeedbackItemProps {
  /** The feedback to render */
  item: AssignmentFeedback;
  /** The full student response text (used to pull out the selection) */
  writingResponse?: string;
  teacherView?: boolean;
  /** Called when the item is clicked (e.g. to toggle “active”) */
  onClick?: (id: string) => void;
  onUpdate?: (feedback: AssignmentFeedback) => void;
  /** Called when the delete‐icon is clicked */
  onDelete?: (id: string) => void;
}

const FeedbackItem: React.FC<FeedbackItemProps> = ({
  item,
  writingResponse,
  teacherView = false,
  onUpdate,
  onDelete,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { id, correction_type, text, translation } = item;
  const { language } = useLanguage();
  const makeApiCall = useApiCall();

  const handleDeleteFeedback = () => {
    makeApiCall(teacherService.deleteAssignmentFeedback, item.id)
      .then((resp) => onDelete && item.id && onDelete(item.id))
      .catch((error) => alert('Failed to delete feedback! ' + error));
  };

  const handleFeedbackItemClick = () => {
    const matching = document.querySelectorAll(
      `[data-feedback-id="${item.id}"]`,
    );
    if (!matching.length) return;

    matching.forEach((el) => el.classList.toggle('active'));
  };

  const handleDoubleClick = () => {
    if (!teacherView) return;
    setShowModal(true);
  };

  const displayText = useMemo(
    () => (language.name === 'English' ? text : translation ?? text),
    [language, text, translation],
  );

  return (
    <>
      <div
        className={`feedback-item ${correction_type}`}
        data-feedback-id={id}
        onClick={handleFeedbackItemClick}
        onDoubleClick={handleDoubleClick}
      >
        <div className="feedback-type">{correction_type}</div>

        <div className="feedback-text">{displayText}</div>

        {teacherView && id && (
          <IconButton
            icon={faTrash}
            color="var(--dark-pink)"
            className="x-modal-button"
            type="transparent"
            onClick={handleDeleteFeedback}
            requiresConfirmation={true}
            confirmationMessage="Delete this comment?"
          />
        )}
      </div>
      {onUpdate && teacherView && (
        <AnnotationModal
          originalFeedback={item}
          submissionId={item.submission_id}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          selection={null}
          onSubmit={onUpdate}
        />
      )}
    </>
  );
};

export default FeedbackItem;
